'use client'

import FooterShape from "@/icons/footer-shape.svg";
import LucyWhite from "@/icons/lucy-white.svg";
import Facebook from "@/icons/facebook.svg";
import Linkedin from "@/icons/linkedin.svg";
import Instagram from "@/icons/instagram.svg";
import AngleDown from "@/icons/angleDown.svg";
import { getTranslation } from "@/i18n/get-translation";
import Link from "next/link";
import { scrollToElement } from "@/utils/utils";
import { resetConsent } from "@/utils/analytics";

interface IFooter {
  locale: string;
}
export default function Footer({ locale }: IFooter) {
  const t = getTranslation(locale, "footer");
  return (
    <section className="relative text-white bg-(image:--footer-gradient)">
      <FooterShape className="w-[calc(100%+4px)] bg-white relative z-10" />
      <div className="flex flex-col justify-end inset-0 -mt-80 relative z-20 max-l:-mt-60 max-md:-mt-20">
        <div className="flex items-end gap-8 justify-between px-20 max-md:px-7 mb-16 pr-60 max-l:flex-wrap max-l:justify-start">
          <div className="flex-[0_0_auto] max-w-full">
            <LucyWhite className="w-[8.063rem]" />
            <p className="text-xl font-light max-w-sm my-4">
              {t("description")}
            </p>
            <nav className="flex gap-4 text-lg">
              <a
                href="https://www.facebook.com/medlucyofficial"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Follow us on Facebook"
                className="w-10 h-10 flex items-center justify-center leading-0 bg-white/25 rounded-full"
              >
                <Facebook />
              </a>
              <a
                href="https://www.instagram.com/medlucyofficial/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Follow us on Instagram"
                className="w-10 h-10 flex items-center justify-center leading-0 bg-white/25 rounded-full"
              >
                <Instagram />
              </a>
              <a
                href="https://www.linkedin.com/company/medlucy/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Follow us on LinkedIn"
                className="w-10 h-10 flex items-center justify-center leading-0 bg-white/25 rounded-full"
              >
                <Linkedin />
              </a>
            </nav>
          </div>
          <div className="flex whitespace-nowrap flex-[0_0_auto] max-md:mt-5">
            <div className="flex-[0_0_auto] px-6 max-md:px-0">
              <Link href={`/${locale}/request-demo`} className="flex items-center mb-4">
                {t("requestDemo")}
                <AngleDown className="w-3 -rotate-90 ml-3" />
              </Link>
              <Link href="#what_is_lucy" onClick={()=>scrollToElement("what_is_lucy")} className="flex items-center mb-4">
                {t("whatIsLucy")}
                <AngleDown className="w-3 -rotate-90 ml-3" />
              </Link>
              <Link href="#why_is_lucy" onClick={()=>scrollToElement("why_is_lucy")} className="flex items-center mb-4">
                {t("whyLucy")}
                <AngleDown className="w-3 -rotate-90 ml-3" />
              </Link>
              <Link href={`/${locale}/blog`} className="flex items-center">
                {t("blog")}
                <AngleDown className="w-3 -rotate-90 ml-3" />
              </Link>
            </div>
            <div className="flex-[0_0_auto] px-6">
              <Link href={`/${locale}/faq`} className="flex items-center mb-4">
                {t("faqs")}
                <AngleDown className="w-3 -rotate-90 ml-3" />
              </Link>
              <Link href={`/${locale}/help-support`} className="flex items-center mb-4">
                {t("helpSupport")}
                <AngleDown className="w-3 -rotate-90 ml-3" />
              </Link>
              <Link href={`/${locale}/sitemap`} className="flex items-center mb-4">
                {t("sitemap")}
                <AngleDown className="w-3 -rotate-90 ml-3" />
              </Link>
              <Link href={`/${locale}/contact-us`} className="flex items-center">
                {t("contact")}
                <AngleDown className="w-3 -rotate-90 ml-3" />
              </Link>
            </div>
          </div>
        </div>
        <div className="px-20 max-md:px-7">
          <div className="flex border-t max-md:border-t-0 border-white/50 py-4 max-md:flex-wrap">
            <div className="font-light text-xl max-md:basis-full max-md:order-2">
              {t("2023Lucy")}
            </div>
            <div className="flex ml-auto max-md:basis-full max-md:ml-0 max-md:order-1 max-md:border-b max-md:border-white/50 max-md:pb-4 max-md: max-md:mb-4 text-lg">
              <Link
                href={`/${locale}/privacy-policy`} 
                className="relative pr-6 after:top-2/4 after:absolute after:right-0 after:h-5.5 after:bg-white after:w-[0.063rem] after:-translate-y-2/4"
              >
                {t("privacyPolicy")}
              </Link>
              <Link href={`/${locale}/terms-condition`} className="relative pl-6 pr-6 after:top-2/4 after:absolute after:right-0 after:h-5.5 after:bg-white after:w-[0.063rem] after:-translate-y-2/4">
                {t("termsConditions")}
              </Link>
              <button
                onClick={resetConsent}
                className="relative pl-6 text-white text-lg bg-transparent border-none cursor-pointer hover:underline"
              >
                {t("cookieSettings")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
